import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { joApi, joErr } from '@/services/processingJoApi';

export const sumOf = (m) => Object.values(m || {}).reduce((a, v) => a + (Number(v) || 0), 0);
export const rowSum = (r) => sumOf(r?.own) + sumOf(r?.parent);
const digits = (v) => String(v ?? '').replace(/[^0-9]/g, '');
const picked = (list) => Object.fromEntries((list || []).filter((c) => c.pieces > 0).map((c) => [c.color_name, c.pieces]));

/** Per-sort colour quantities ({own, parent}): a typed row total is split colour-wise on the server (own stock first,
 *  then the parent sort); editing a colour re-totals the row. `avail` = {own, parent} lists of {color_name, free_pcs}. */
export const useColourQty = () => {
  const [qty, setQty] = useState({});
  const [typed, setTyped] = useState({});
  const [splitting, setSplitting] = useState({});
  const timers = useRef({});
  const seq = useRef({});
  const stop = (k) => { seq.current[k] = (seq.current[k] || 0) + 1; clearTimeout(timers.current[k]); return seq.current[k]; };
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const reset = useCallback(() => {
    Object.values(timers.current).forEach(clearTimeout);
    timers.current = {}; seq.current = {};
    setQty({}); setTyped({}); setSplitting({});
  }, []);

  const runSplit = async (a, pieces, avail, my) => {
    const k = a.sort_no;
    try {
      const r = await joApi.colourSplit({ pieces, need_assortment: !!a.need_assortment, colors: avail.own, parent_colors: avail.parent });
      if (seq.current[k] !== my) return;
      const m = { own: picked(r.colors), parent: picked(r.parent_colors) };
      setQty((p) => ({ ...p, [k]: m }));
      setTyped((p) => ({ ...p, [k]: String(rowSum(m) || '') }));
      if (r.shortfall > 0) toast.warning(`Sort ${k}: only ${rowSum(m)} pcs free — split capped`);
    } catch (e) {
      if (seq.current[k] === my) toast.error(joErr(e, 'Could not split by colour'));
    } finally {
      if (seq.current[k] === my) setSplitting((p) => ({ ...p, [k]: false }));
    }
  };

  const setTotal = (a, raw, avail) => {
    const k = a.sort_no;
    const v = digits(raw);
    const my = stop(k);
    setTyped((p) => ({ ...p, [k]: v }));
    if (!Number(v)) { setQty((p) => ({ ...p, [k]: {} })); setSplitting((p) => ({ ...p, [k]: false })); return; }
    setSplitting((p) => ({ ...p, [k]: true }));
    timers.current[k] = setTimeout(() => runSplit(a, Number(v), avail, my), 350);
  };

  const setColour = (a, src, color, raw, max) => {
    const k = a.sort_no;
    stop(k);
    const cur = qty[k] || {};
    const m = { ...cur, [src]: { ...(cur[src] || {}), [color]: Math.min(Number(digits(raw)) || 0, Math.max(0, max)) } };
    setSplitting((p) => ({ ...p, [k]: false }));
    setQty((p) => ({ ...p, [k]: m }));
    setTyped((p) => ({ ...p, [k]: String(rowSum(m) || '') }));
  };

  return { qty, typed, splitting, setTotal, setColour, reset, busy: Object.values(splitting).some(Boolean) };
};
